import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const ChatBubble = ({ isOpen = false, onToggle }) => {
  const [showPreview, setShowPreview] = useState(false)
  const { t } = useTranslation()
  
  // Chatbot configuration from environment variables and translations
  const chatbotConfig = {
    url: import.meta.env.VITE_CHATBOT_URL || 'https://chat.satr-app.com/almikial-website-qbrz366',
    title: t('chatbot.title'),
    previewMessage: t('chatbot.previewMessage'),
    enabled: import.meta.env.VITE_CHATBOT_ENABLED !== 'false'
  }

  // Show preview message after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen) {
        setShowPreview(true)
      }
    }, 5000)

    return () => clearTimeout(timer)
  }, [isOpen])

  // Hide preview when chat is opened
  useEffect(() => {
    if (isOpen) {
      setShowPreview(false)
    }
  }, [isOpen])

  const toggleChat = () => {
    if (onToggle) {
      onToggle(!isOpen)
    }
    setShowPreview(false)
  }

  // Don't render chatbot if disabled
  if (!chatbotConfig.enabled) {
    return null;
  }

  return (
    <>
      {/* Preview Message */}
      {showPreview && !isOpen && (
        <div 
          className="preview-message"
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '20px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '12px 16px',
            borderRadius: '25px',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: '500',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            maxWidth: '280px',
            zIndex: 9998,
            animation: 'fadeInUp 0.5s ease-out',
            cursor: 'pointer'
          }}
          onClick={() => setShowPreview(false)}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>💬</span>
            <span>{chatbotConfig.previewMessage}</span>
          </div>
          {/* Close button */}
          <button 
            onClick={(e) => {
              e.stopPropagation()
              setShowPreview(false)
            }}
            style={{
              position: 'absolute',
              top: '4px',
              right: '8px',
              background: 'none',
              border: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '16px',
              cursor: 'pointer',
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="chat-window"
          style={{
            position: 'fixed',
            bottom: '100px',
            right: '20px',
            width: '400px',
            height: '600px',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '25px',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            backdropFilter: 'blur(40px)',
            zIndex: 9998,
            overflow: 'hidden',
            animation: 'slideInUp 0.3s ease-out'
          }}>
          {/* Chat Header - Ultra Glassy */}
          <div style={{
            padding: '16px 24px',
            borderBottom: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            backdropFilter: 'blur(25px)',
            borderRadius: '25px 25px 0 0'
          }}>
            <div style={{
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif',
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.8)'
            }}>
              {chatbotConfig.title}
            </div>
          </div>

          {/* Chat Content Area - Maximum Glassy Transparent */}
          <div style={{
            height: 'calc(100% - 70px)',
            backgroundColor: 'rgba(255, 255, 255, 0.01)',
            backdropFilter: 'blur(35px)',
            position: 'relative',
            borderRadius: '0 0 25px 25px',
            overflow: 'hidden',
            border: 'none'
          }}>
            {/* Embedded Iframe */}
            {chatbotConfig.enabled && (
              <iframe
                src={chatbotConfig.url}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: '0 0 25px 25px',
                opacity: '0.9',
                filter: 'contrast(1.1) brightness(1.05)'
              }}
                title={`${chatbotConfig.title} Chat`}
              />
            )}
          </div>
        </div>
      )}

      {/* Chat Bubble Button with Animated Border */}
      <div 
        className="chat-bubble-container"
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 9999
        }}>
        {/* Animated Border */}
        <div style={{
          position: 'absolute',
          top: '-3px',
          left: '-3px',
          width: '66px',
          height: '66px',
          borderRadius: '33px',
          background: 'conic-gradient(from 0deg, transparent, rgba(255, 255, 255, 0.8), transparent, rgba(0, 0, 0, 0.6), transparent)',
          animation: 'rotateBorder 3s linear infinite',
          zIndex: -1
        }} />
        
        {/* Chat Bubble Button */}
        <button
          className="chat-bubble"
          onClick={toggleChat}
          style={{
            position: 'relative',
            width: '60px',
            height: '60px',
            borderRadius: '30px',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          transition: 'all 0.3s ease',
          color: 'white',
          backdropFilter: 'blur(20px)',
          zIndex: 1,
          userSelect: 'none',
          WebkitUserSelect: 'none',
          MozUserSelect: 'none',
          msUserSelect: 'none'
          }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'scale(1.1)'
            e.target.style.boxShadow = '0 12px 40px rgba(0, 0, 0, 0.4)'
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.8)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)'
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'scale(1)'
            e.target.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.3)'
            e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.7)'
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'
          }}
        >
          {isOpen ? (
            /* Close Icon */
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          ) : (
            /* Chat Icon */
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
            </svg>
          )}
        </button>
      </div>

      <style>{`
        @keyframes fadeInUp {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          0% {
            opacity: 0;
            transform: translateY(30px) scale(0.9);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes rotateBorder {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          /* Chat window on mobile */
          .chat-window {
            width: calc(100vw - 20px) !important;
            height: 70vh !important;
            bottom: 90px !important;
            right: 10px !important;
            left: 10px !important;
            border-radius: 20px !important;
          }
          
          /* Chat bubble container on mobile */
          .chat-bubble-container {
            bottom: 15px !important;
            right: 15px !important;
          }
          
          /* Animated border on mobile */
          .chat-bubble-container > div:first-child {
            top: -2px !important;
            left: -2px !important;
            width: 54px !important;
            height: 54px !important;
            border-radius: 27px !important;
          }
          
          /* Chat bubble button on mobile */
          .chat-bubble {
            width: 50px !important;
            height: 50px !important;
            border-radius: 25px !important;
          }
          
          /* Preview message on mobile */
          .preview-message {
            bottom: 80px !important;
            right: 15px !important;
            max-width: 250px !important;
            font-size: 13px !important;
            padding: 10px 14px !important;
            border-radius: 20px !important;
          }
        }
      `}</style>
    </>
  )
}

export default ChatBubble
